import { apiFetch } from '..//auth/AuthContext';
/**
 * CoinRegistryContext — Single source of truth for available coins + genesis dates.
 *
 * Queries ClickHouse for all coins present in binance_raw and derives:
 *   - availableCoins: string[]                (ALL onboarded coins)
 *   - enabledCoins: string[]                  (coins not disabled — use this for display)
 *   - disabledCoins: Set<string>              (coins marked disabled in dashboard_meta.coin_config)
 *   - coinGenesis: Record<string, string>     (coin → earliest data date YYYY-MM-DD)
 *   - coinColors: Record<string, string>      (coin → brand color, with fallback)
 *
 * Disabled state is persisted in ClickHouse (dashboard_meta.coin_config) so it
 * survives browser refreshes and works across multiple users/devices.
 *
 * toggleCoin() calls the onboard server to write to ClickHouse, then refreshes state.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

// Credentials are injected server-side by the Nginx /ch proxy.
async function chQuery<T = Record<string, unknown>>(sql: string): Promise<T[]> {
  const r = await apiFetch('/ch', {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: sql.trim() + '\nFORMAT JSON',
  });
  if (!r.ok) throw new Error(`ClickHouse error: ${r.status}`);
  const j = await r.json();
  return (j.data ?? []) as T[];
}

export interface CoinRegistryData {
  /** All onboarded coin symbols (including disabled ones) */
  availableCoins: string[];
  /** Only enabled coins — use for display in AddTab, DeleteTab, EditTab pill grid */
  enabledCoins: string[];
  /** Set of disabled coin symbols */
  disabledCoins: Set<string>;
  /** Coin → earliest data date (YYYY-MM-DD) */
  coinGenesis: Record<string, string>;
  /** Coin → hex color */
  coinColors: Record<string, string>;
  /** Whether the initial load is in progress */
  loading: boolean;
  /** Re-fetch from ClickHouse. Call after onboarding completes. */
  refreshRegistry: () => Promise<void>;
  /** Toggle a single coin's enabled/disabled state (persisted to ClickHouse) */
  toggleCoin: (coin: string, enabled: boolean) => Promise<void>;
  /** Bulk toggle coins (persisted to ClickHouse) */
  bulkToggle: (coins: string[], enabled: boolean) => Promise<void>;
}

/** Fallback brand colors for well-known coins */
const KNOWN_COLORS: Record<string, string> = {
  AAVE: '#B6509E',
  ADA: '#0033AD',
  ARB: '#28A0F0',
  AVAX: '#E84142',
  BCH: '#8DC351',
  BNB: '#F3BA2F',
  BTC: '#F7931A',
  DOT: '#E6007A',
  ENA: '#7B5BF0',
  EOS: '#443F54',
  ETC: '#328332',
  ETH: '#627EEA',
  FET: '#1D2951',
  GRT: '#6747ED',
  ICP: '#29ABE2',
  IMX: '#00BFFF',
  LDO: '#F69988',
  LINK: '#2A5ADA',
  LTC: '#345D9D',
  MANTA: '#1FC7D4',
  MKR: '#1AAB9B',
  OP: '#FF0420',
  PENDLE: '#EAFF00',
  RENDER: '#000000',
  SOL: '#9945FF',
  STX: '#5546FF',
  TAO: '#000000',
  TRX: '#FF0013',
  UNI: '#FF007A',
  WLD: '#000000',
  XLM: '#14B6E7',
  XRP: '#23292F',
};

const DEFAULT_COLOR = '#8b949e';

const EMPTY_SET = new Set<string>();

const CoinRegistryContext = createContext<CoinRegistryData>({
  availableCoins: [],
  enabledCoins: [],
  disabledCoins: EMPTY_SET,
  coinGenesis: {},
  coinColors: {},
  loading: true,
  refreshRegistry: async () => {},
  toggleCoin: async () => {},
  bulkToggle: async () => {},
});

export function CoinRegistryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [availableCoins, setAvailableCoins] = useState<string[]>([]);
  const [disabledCoins, setDisabledCoins] = useState<Set<string>>(EMPTY_SET);
  const [coinGenesis, setCoinGenesis] = useState<Record<string, string>>({});
  const [coinColors, setCoinColors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const enabledCoins = useMemo(
    () => availableCoins.filter((c) => !disabledCoins.has(c)),
    [availableCoins, disabledCoins],
  );

  const refreshRegistry = useCallback(async () => {
    try {
      // Fetch coins + genesis from binance_raw
      const rows = await chQuery<{ symbol: string; earliest: string }>(`
        SELECT symbol, min(earliest) AS earliest
        FROM (
          SELECT symbol, min(open_time) AS earliest FROM binance_raw.spot_ohlcv_5m GROUP BY symbol
          UNION ALL
          SELECT symbol, min(open_time) AS earliest FROM binance_raw.futures_ohlcv_5m GROUP BY symbol
          UNION ALL
          SELECT symbol, min(open_time) AS earliest FROM binance_raw.spot_ohlcv_1m GROUP BY symbol
          UNION ALL
          SELECT symbol, min(funding_time) AS earliest FROM binance_raw.funding_rate GROUP BY symbol
        )
        GROUP BY symbol
        ORDER BY symbol
      `);

      const coins: string[] = [];
      const genesis: Record<string, string> = {};
      const colors: Record<string, string> = {};

      for (const row of rows) {
        const sym = row.symbol.replace(/USDT$/, '');
        coins.push(sym);
        genesis[sym] = row.earliest.slice(0, 10);
        colors[sym] = KNOWN_COLORS[sym] || DEFAULT_COLOR;
      }

      setAvailableCoins(coins);
      setCoinGenesis(genesis);
      setCoinColors(colors);

      // Fetch disabled state from dashboard_meta.coin_config
      try {
        const configRows = await chQuery<{ symbol: string; enabled: number }>(`
          SELECT symbol, enabled
          FROM dashboard_meta.coin_config FINAL
          WHERE enabled = 0
        `);
        setDisabledCoins(new Set(configRows.map((r) => r.symbol)));
      } catch {
        // Table may not exist yet — treat all as enabled
        setDisabledCoins(EMPTY_SET);
      }
    } catch {
      // On failure, keep whatever we had before
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRegistry();
  }, [refreshRegistry]);

  const toggleCoin = useCallback(async (coin: string, enabled: boolean) => {
    const sym = coin.toUpperCase();
    try {
      const params = new URLSearchParams({
        coin: sym,
        enabled: enabled ? '1' : '0',
      });
      await apiFetch(`/onboard/coins/toggle?${params}`, { method: 'POST' });
    } catch {
      // Proceed with optimistic update even if server fails
    }
    // Optimistic update
    setDisabledCoins((prev) => {
      const next = new Set(prev);
      if (enabled) next.delete(sym);
      else next.add(sym);
      return next;
    });
  }, []);

  const bulkToggle = useCallback(async (coins: string[], enabled: boolean) => {
    const syms = coins.map((c) => c.toUpperCase());
    try {
      const params = new URLSearchParams({
        coins: syms.join(','),
        enabled: enabled ? '1' : '0',
      });
      await apiFetch(`/onboard/coins/bulk-toggle?${params}`, {
        method: 'POST',
      });
    } catch {
      // Proceed with optimistic update
    }
    setDisabledCoins((prev) => {
      const next = new Set(prev);
      for (const sym of syms) {
        if (enabled) next.delete(sym);
        else next.add(sym);
      }
      return next;
    });
  }, []);

  return (
    <CoinRegistryContext.Provider
      value={{
        availableCoins,
        enabledCoins,
        disabledCoins,
        coinGenesis,
        coinColors,
        loading,
        refreshRegistry,
        toggleCoin,
        bulkToggle,
      }}
    >
      {children}
    </CoinRegistryContext.Provider>
  );
}

export function useCoinRegistry(): CoinRegistryData {
  return useContext(CoinRegistryContext);
}
